package data.graphql

/**
 * Comprehensive GraphQL queries for GitHub v4 API
 * Includes organization management, PR insights, SSO status, and invitations
 */
object GitHubGraphQLQueries {

    /**
     * Get current user's organizations with SSO and membership details
     */
    const val GET_USER_ORGANIZATIONS = """
        query GetUserOrganizations(${"$"}cursor: String) {
            viewer {
                login
                organizations(first: 100, after: ${"$"}cursor) {
                    pageInfo {
                        hasNextPage
                        endCursor
                    }
                    totalCount
                    edges {
                        node {
                            id
                            login
                            name
                            description
                            avatarUrl
                            url
                            location
                            email
                            twitterUsername
                            websiteUrl
                            createdAt
                            updatedAt
                            repositories {
                                totalCount
                            }
                            membersWithRole {
                                totalCount
                            }
                            isVerified
                            samlIdentityProvider {
                                ssoUrl
                            }
                            teams(first: 10) {
                                nodes {
                                    id
                                    name
                                    slug
                                    description
                                    privacy
                                    url
                                }
                            }
                            viewerCanAdminister
                            viewerCanCreateRepositories
                            viewerCanCreateTeams
                            viewerIsAMember
                        }
                    }
                }
            }
        }
    """
    
    /**
     * Get user's organization memberships (including restricted organizations)
     * This query can help identify organizations that may be blocking OAuth access
     */
    const val GET_USER_ORGANIZATION_MEMBERSHIPS = """
        query GetUserOrganizationMemberships {
            viewer {
                login
                name
                email
                avatarUrl
                organizationVerifiedDomainEmails
            }
        }
    """

    /**
     * Get pull requests with enhanced insights across organizations
     */
    const val GET_PULL_REQUESTS_INSIGHTS = """
        query GetPullRequestsInsights(${"$"}query: String!, ${"$"}cursor: String) {
            search(type: ISSUE, query: ${"$"}query, first: 50, after: ${"$"}cursor) {
                pageInfo {
                    hasNextPage
                    endCursor
                }
                issueCount
                edges {
                    node {
                        ... on PullRequest {
                            id
                            number
                            title
                            url
                            state
                            isDraft
                            createdAt
                            updatedAt
                            author {
                                login
                                ... on User {
                                    name
                                    avatarUrl
                                    url
                                }
                            }
                            repository {
                                id
                                name
                                nameWithOwner
                                url
                                isPrivate
                                primaryLanguage {
                                    name
                                }
                                owner {
                                    login
                                    ... on Organization {
                                        name
                                        avatarUrl
                                        url
                                        isVerified
                                        samlIdentityProvider {
                                            ssoUrl
                                        }
                                    }
                                }
                            }
                            labels(first: 20) {
                                nodes {
                                    name
                                    color
                                    description
                                }
                            }
                            reviewDecision
                            comments {
                                totalCount
                            }
                            additions
                            deletions
                            changedFiles
                            mergeable
                            baseRefName
                            headRefName
                            assignees(first: 10) {
                                nodes {
                                    login
                                    name
                                    avatarUrl
                                    url
                                }
                            }
                            reviewRequests(first: 10) {
                                nodes {
                                    requestedReviewer {
                                        ... on User {
                                            login
                                            name
                                            avatarUrl
                                            url
                                        }
                                        ... on Team {
                                            name
                                            slug
                                        }
                                    }
                                }
                            }
                            reviews(first: 20) {
                                nodes {
                                    id
                                    state
                                    submittedAt
                                    author {
                                        login
                                        ... on User {
                                            name
                                            avatarUrl
                                            url
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    """

    /**
     * Get detailed organization information with teams and repositories
     */
    const val GET_ORGANIZATION_DETAILS = """
        query GetOrganizationDetails(${"$"}login: String!) {
            organization(login: ${"$"}login) {
                id
                login
                name
                description
                avatarUrl
                url
                location
                email
                twitterUsername
                websiteUrl
                createdAt
                updatedAt
                repositoryCount: repositories {
                    totalCount
                }
                membersWithRole {
                    totalCount
                }
                isVerified
                samlIdentityProvider {
                    ssoUrl
                }
                teams(first: 100) {
                    pageInfo {
                        hasNextPage
                        endCursor
                    }
                    nodes {
                        id
                        name
                        slug
                        description
                        privacy
                        url
                        members {
                            totalCount
                        }
                        repositories {
                            totalCount
                        }
                        organization {
                            login
                            name
                        }
                    }
                }
                recentRepositories: repositories(first: 20, orderBy: {field: UPDATED_AT, direction: DESC}) {
                    nodes {
                        name
                        nameWithOwner
                        url
                        isPrivate
                        updatedAt
                        primaryLanguage {
                            name
                        }
                        pullRequests(states: [OPEN]) {
                            totalCount
                        }
                        issues(states: [OPEN]) {
                            totalCount
                        }
                    }
                }
            }
        }
    """

    /**
     * Get user's team memberships across organizations
     * Note: teams field doesn't exist on User type. 
     * Team access is through organization membership.
     */
    const val GET_USER_TEAMS = """
        query GetUserTeams(${"$"}cursor: String) {
            viewer {
                login
                organizations(first: 100, after: ${"$"}cursor) {
                    pageInfo {
                        hasNextPage
                        endCursor
                    }
                    nodes {
                        login
                        name
                        avatarUrl
                        url
                        teams(first: 100) {
                            nodes {
                                id
                                name
                                slug
                                description
                                privacy
                                url
                                members {
                                    totalCount
                                }
                                repositories {
                                    totalCount
                                }
                            }
                        }
                    }
                }
            }
        }
    """

    /**
     * Check SSO authentication status for an organization
     */
    const val GET_SSO_STATUS = """
        query GetSSOStatus(${"$"}organizationLogin: String!) {
            organization(login: ${"$"}organizationLogin) {
                login
                name
                samlIdentityProvider {
                    ssoUrl
                }
                viewerCanAdminister
                viewerCanCreateRepositories
                viewerCanCreateTeams
                viewerIsAMember
            }
        }
    """

    /**
     * Get pending organization invitations for the current user
     * Note: Individual user invitations are not accessible via GraphQL.
     * This query returns the user's basic info for UI consistency.
     */
    const val GET_PENDING_INVITATIONS = """
        query GetPendingInvitations {
            viewer {
                login
                name
                email
                avatarUrl
            }
        }
    """

    /**
     * Get organization activity summary (PRs, issues, recent activity)
     */
    const val GET_ORGANIZATION_ACTIVITY = """
        query GetOrganizationActivity(${"$"}organizationLogin: String!) {
            organization(login: ${"$"}organizationLogin) {
                login
                name
                repositories(first: 100, orderBy: {field: UPDATED_AT, direction: DESC}) {
                    totalCount
                    nodes {
                        name
                        updatedAt
                        pullRequests(states: [OPEN]) {
                            totalCount
                        }
                        issues(states: [OPEN]) {
                            totalCount
                        }
                    }
                }
                teams {
                    totalCount
                }
                membersWithRole {
                    totalCount
                }
            }
            search(type: ISSUE, query: "org:${"$"}organizationLogin is:pr") {
                issueCount
            }
            search(type: ISSUE, query: "org:${"$"}organizationLogin is:issue") {
                issueCount
            }
        }
    """

    /**
     * Get repository-specific PR insights
     */
    const val GET_REPOSITORY_PRS = """
        query GetRepositoryPRs(${"$"}owner: String!, ${"$"}name: String!, ${"$"}cursor: String) {
            repository(owner: ${"$"}owner, name: ${"$"}name) {
                id
                name
                nameWithOwner
                url
                isPrivate
                primaryLanguage {
                    name
                }
                owner {
                    login
                    ... on Organization {
                        name
                        avatarUrl
                        url
                        isVerified
                    }
                }
                pullRequests(first: 50, after: ${"$"}cursor, orderBy: {field: UPDATED_AT, direction: DESC}) {
                    pageInfo {
                        hasNextPage
                        endCursor
                    }
                    totalCount
                    nodes {
                        id
                        number
                        title
                        url
                        state
                        isDraft
                        createdAt
                        updatedAt
                        author {
                            login
                            ... on User {
                                name
                                avatarUrl
                                url
                            }
                        }
                        labels(first: 20) {
                            nodes {
                                name
                                color
                                description
                            }
                        }
                        reviewDecision
                        totalCommentsCount
                        additions
                        deletions
                        changedFiles
                        mergeable
                        baseRefName
                        headRefName
                        assignees(first: 10) {
                            nodes {
                                login
                                name
                                avatarUrl
                                url
                            }
                        }
                        reviewRequests(first: 10) {
                            nodes {
                                requestedReviewer {
                                    ... on User {
                                        login
                                        name
                                        avatarUrl
                                        url
                                    }
                                }
                            }
                        }
                        reviews(first: 20) {
                            nodes {
                                id
                                state
                                submittedAt
                                author {
                                    login
                                    ... on User {
                                        name
                                        avatarUrl
                                        url
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    """

    /**
     * Build search query for pull requests based on filter criteria
     */
    fun buildPRSearchQuery(
        author: String? = null,
        organizations: List<String> = emptyList(),
        teams: List<String> = emptyList(),
        excludeAuthor: String? = null,
        state: String = "open",
        involvedUser: String? = null
    ): String {
        val queryParts = mutableListOf<String>()
        
        queryParts.add("is:pr")
        queryParts.add("state:$state")
        
        when {
            author != null -> queryParts.add("author:$author")
            excludeAuthor != null -> queryParts.add("-author:$excludeAuthor")
            involvedUser != null -> queryParts.add("involves:$involvedUser")
        }
        
        // Add organization filters
        if (organizations.isNotEmpty()) {
            val orgQuery = organizations.joinToString(" ") { "org:$it" }
            queryParts.add("($orgQuery)")
        }
        
        // Add team filters (teams are specified as org/team)
        teams.forEach { team ->
            queryParts.add("team:$team")
        }
        
        return queryParts.joinToString(" ")
    }

    /**
     * Build variables for GraphQL query execution
     * Converts all values to strings for Edge Function compatibility
     */
    fun buildQueryVariables(vararg pairs: Pair<String, Any?>): Map<String, String> {
        return pairs.mapNotNull { (key, value) ->
            when (value) {
                null -> null
                is String -> key to value
                else -> key to value.toString()
            }
        }.toMap()
    }
}
